import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Paper, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import GenericDataGrid from '../../../components/genericTable';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { compactTableActionButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { SoilMoistureThresholdModal } from '../climate/baselineModals';
import { CropAliasModal, MspPriceModal } from './marketReferenceModals';
import {
    aliasDraftFor,
    aliasStatus,
    getReferenceCoverage,
    mspDraftFor,
    mspStatus,
    priceStatus,
    rainfallStatus,
    thresholdDraftFor,
    thresholdStatus,
} from './marketReference.service';

const textCellSx = { fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' };

const cardSx = { p: 3, borderRadius: '16px', border: '1px solid #DFE5F6', backgroundColor: '#FFFFFF' };

const captionSx = { fontSize: 12, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#625B71', fontWeight: 600 };

const TONES = {
    ok: { color: '#4F8506', bgcolor: '#EEF7E6' },
    missing: { color: '#DA342E', bgcolor: '#FDECEA' },
    na: { color: '#625B71', bgcolor: '#F1F1F1' },
    info: { color: '#1D4ED8', bgcolor: '#E8F0FE' },
};

function StatusChip({ status, onAdd, addLabel }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
            <Chip size="small" label={status.label} sx={{ ...TONES[status.tone], fontWeight: 600, borderRadius: '8px' }} />
            {status.tone === 'missing' && onAdd ? (
                <Button sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx, minWidth: 0, px: 1 }} onClick={onAdd}>
                    {addLabel}
                </Button>
            ) : null}
        </Box>
    );
}

function GapTile({ label, value, tone }) {
    return (
        <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
            <Typography sx={captionSx}>{label}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 0.5, color: value > 0 ? TONES[tone].color : '#4F8506' }}>
                {value.toLocaleString('en-IN')}
            </Typography>
        </Paper>
    );
}

function formatDateTime(value) {
    if (!value) return '';
    return new Date(value).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Live check of the reference data behind the weather and market signals: every crop and state
 * farmers are on, and what each one has. Nothing here is stored — it is worked out from the
 * database on every load, so a crop a DARE added this morning shows up straight away.
 */
function ReferenceCoverage() {
    const [coverage, setCoverage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [mspPrefill, setMspPrefill] = useState(null);
    const [aliasPrefill, setAliasPrefill] = useState(null);
    const [thresholdPrefill, setThresholdPrefill] = useState(null);

    const [cropPage, setCropPage] = useState(0);
    const [cropSize, setCropSize] = useState(10);
    const [regionPage, setRegionPage] = useState(0);
    const [regionSize, setRegionSize] = useState(10);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const load = useCallback(async () => {
        setLoading(true);
        const result = await getReferenceCoverage();
        if (result.success) {
            setCoverage(result.data);
            setError('');
        } else {
            setError(result.error.message);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saved = (message, close) => async () => {
        close(null);
        await load();
        notify(message);
    };

    const crops = coverage?.crops || [];
    const regions = coverage?.regions || [];
    const gaps = coverage?.gaps || { cropsWithoutAlias: 0, cropsWithoutMsp: 0, cropsWithoutThreshold: 0, farmersAffected: 0 };

    const cropColumns = [
        {
            field: 'cropName',
            headerName: 'Crop',
            flex: 1,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>
                    {params.row.canonicalName && params.row.canonicalName !== params.value ? (
                        <Typography sx={{ fontSize: 13, color: '#625B71' }}>means {params.row.canonicalName}</Typography>
                    ) : null}
                </Box>
            ),
        },
        {
            field: 'countryName',
            headerName: 'Country',
            flex: 0.6,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'farmers',
            headerName: 'Farmers',
            flex: 0.5,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{Number(params.value || 0).toLocaleString('en-IN')}</Typography>
            ),
        },
        {
            field: 'aliasKnown',
            headerName: 'Name',
            flex: 0.9,
            minWidth: 190,
            sortable: false,
            renderCell: (params) => (
                <StatusChip status={aliasStatus(params.row)} addLabel="Map" onAdd={() => setAliasPrefill(aliasDraftFor(params.row))} />
            ),
        },
        {
            field: 'priceSource',
            headerName: 'Mandi prices',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => <StatusChip status={priceStatus(params.row)} />,
        },
        {
            field: 'msp',
            headerName: 'MSP',
            flex: 0.9,
            minWidth: 190,
            sortable: false,
            renderCell: (params) => (
                <StatusChip status={mspStatus(params.row)} addLabel="Add" onAdd={() => setMspPrefill(mspDraftFor(params.row))} />
            ),
        },
        {
            field: 'soilMoistureThreshold',
            headerName: 'Soil moisture',
            flex: 0.8,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => (
                <StatusChip
                    status={thresholdStatus(params.row)}
                    addLabel="Add"
                    onAdd={() => setThresholdPrefill(thresholdDraftFor(params.row))}
                />
            ),
        },
    ];

    const regionColumns = [
        {
            field: 'stateName',
            headerName: 'State / region',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value || '-'}</Typography>,
        },
        {
            field: 'countryName',
            headerName: 'Country',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'farmers',
            headerName: 'Farmers',
            flex: 0.6,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{Number(params.value || 0).toLocaleString('en-IN')}</Typography>
            ),
        },
        {
            field: 'rainfallNormal',
            headerName: 'Rainfall normal',
            flex: 1,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => <StatusChip status={rainfallStatus(params.row)} />,
        },
    ];

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        Every crop and state farmers are on, and whether eK Score has the reference data it needs for
                        them. Fill a gap straight from its row.
                        {coverage?.generatedAt ? ` Checked ${formatDateTime(coverage.generatedAt)}.` : ''}
                    </Typography>
                </Box>
                <Box sx={managementHeaderActionSx}>
                    <Button variant="outlined" onClick={load} disabled={loading} startIcon={<Icon icon="tabler:refresh" />}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ borderRadius: '16px', mb: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Rainfall normals are automatic</AlertTitle>
                Each plot's normal comes from 25 years of NASA POWER data for its own area, so no state needs a number
                typed in. This page updates by itself as DAREs add crops and farmers.
            </Alert>

            {error ? <Alert severity="error" sx={{ borderRadius: '12px', mb: 3 }}>{error}</Alert> : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                <GapTile label="Crops without a name mapping" value={gaps.cropsWithoutAlias} tone="missing" />
                <GapTile label="Crops without MSP" value={gaps.cropsWithoutMsp} tone="missing" />
                <GapTile label="Crops without soil moisture" value={gaps.cropsWithoutThreshold} tone="missing" />
                <GapTile label="Farmers affected" value={gaps.farmersAffected} tone="missing" />
            </Box>

            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mb: 1.5 }}>Crops ({crops.length})</Typography>
            <Box sx={{ mb: 4 }}>
                <GenericDataGrid
                    rows={crops
                        .slice(cropPage * cropSize, cropPage * cropSize + cropSize)
                        .map((row, index) => ({ ...row, id: `${row.countryId || ''}:${row.cropId || row.cropName}:${index}` }))}
                    columns={cropColumns}
                    rowCount={crops.length}
                    page={cropPage}
                    pageSize={cropSize}
                    loading={loading}
                    dataGridSx={createManagementTableSx()}
                    tableMinWidth={1220}
                    rowHeight={64}
                    onPaginationChange={(page, size) => {
                        setCropPage(page);
                        setCropSize(size);
                    }}
                />
            </Box>

            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mb: 1.5 }}>
                States and regions ({regions.length})
            </Typography>
            <GenericDataGrid
                rows={regions
                    .slice(regionPage * regionSize, regionPage * regionSize + regionSize)
                    .map((row, index) => ({ ...row, id: `${row.countryId || ''}:${row.stateName}:${index}` }))}
                columns={regionColumns}
                rowCount={regions.length}
                page={regionPage}
                pageSize={regionSize}
                loading={loading}
                dataGridSx={createManagementTableSx()}
                tableMinWidth={720}
                rowHeight={60}
                onPaginationChange={(page, size) => {
                    setRegionPage(page);
                    setRegionSize(size);
                }}
            />

            <MspPriceModal
                open={Boolean(mspPrefill)}
                price={mspPrefill}
                onClose={() => setMspPrefill(null)}
                onSaved={saved('MSP price saved.', setMspPrefill)}
            />
            <CropAliasModal
                open={Boolean(aliasPrefill)}
                alias={aliasPrefill}
                onClose={() => setAliasPrefill(null)}
                onSaved={saved('Crop name saved.', setAliasPrefill)}
            />
            <SoilMoistureThresholdModal
                open={Boolean(thresholdPrefill)}
                threshold={thresholdPrefill}
                onClose={() => setThresholdPrefill(null)}
                onSaved={saved('Soil moisture threshold saved.', setThresholdPrefill)}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default ReferenceCoverage;
